if (!customElements.get('maison-dual-panels')) {
  class MaisonDualPanels extends HTMLElement {
    constructor() {
      super();
      this.measureFrame = 0;
      this.motionFrame = 0;
      this.panels = [];
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleMotionPreference = this.handleMotionPreference.bind(this);
    }

    connectedCallback() {
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.panels = Array.from(this.querySelectorAll('[data-dual-panel]')).map((element) => ({
        element,
        active: false,
        copyCurrent: 0,
        copyTarget: 0,
        mediaCurrent: 0,
        mediaTarget: 0,
      }));

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const panel = this.panels.find((item) => item.element === entry.target);
            if (panel) panel.active = entry.isIntersecting;
          });
          this.requestMeasure();
        },
        { rootMargin: '20% 0px 20% 0px' },
      );

      this.panels.forEach((panel) => this.observer.observe(panel.element));
      window.addEventListener('scroll', this.handleViewportChange, { passive: true });
      window.addEventListener('resize', this.handleViewportChange, { passive: true });
      this.motionPreference.addEventListener('change', this.handleMotionPreference);
      this.handleMotionPreference();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.handleViewportChange);
      window.removeEventListener('resize', this.handleViewportChange);
      this.motionPreference?.removeEventListener('change', this.handleMotionPreference);
      cancelAnimationFrame(this.measureFrame);
      cancelAnimationFrame(this.motionFrame);
    }

    handleViewportChange() {
      if (!this.motionPreference.matches && this.dataset.motion !== 'false') this.requestMeasure();
    }

    handleMotionPreference() {
      if (this.motionPreference.matches || this.dataset.motion === 'false') {
        this.resetMotion();
      } else {
        this.requestMeasure(true);
      }
    }

    requestMeasure(immediate = false) {
      if (this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = 0;
        this.measure(immediate);
      });
    }

    measure(immediate = false) {
      const viewportHeight = window.innerHeight;
      const mobileFactor = window.matchMedia('(max-width: 749px)').matches ? 0.52 : 1;
      const copyDistance = Number(this.dataset.copyDistance || 120) * mobileFactor;
      const mediaDistance = Number(this.dataset.mediaDistance || 36) * mobileFactor;

      this.panels.forEach((panel) => {
        if (!panel.active && !immediate) return;
        const bounds = panel.element.getBoundingClientRect();
        const progress = Math.min(1, Math.max(0, (viewportHeight - bounds.top) / (viewportHeight + bounds.height)));
        panel.copyTarget = -progress * copyDistance;
        panel.mediaTarget = (0.5 - progress) * mediaDistance;

        if (immediate) {
          panel.copyCurrent = panel.copyTarget;
          panel.mediaCurrent = panel.mediaTarget;
        }
      });

      if (immediate) this.writeTransforms();
      else this.startMotion();
    }

    startMotion() {
      if (this.motionFrame) return;

      const animate = () => {
        let moving = false;

        this.panels.forEach((panel) => {
          const nextCopy = panel.copyCurrent + (panel.copyTarget - panel.copyCurrent) * 0.14;
          const nextMedia = panel.mediaCurrent + (panel.mediaTarget - panel.mediaCurrent) * 0.14;

          if (Math.abs(panel.copyTarget - nextCopy) > 0.05 || Math.abs(panel.mediaTarget - nextMedia) > 0.05) {
            moving = true;
          }

          panel.copyCurrent = nextCopy;
          panel.mediaCurrent = nextMedia;
        });

        this.writeTransforms();

        if (moving) this.motionFrame = requestAnimationFrame(animate);
        else this.motionFrame = 0;
      };

      this.motionFrame = requestAnimationFrame(animate);
    }

    writeTransforms() {
      this.panels.forEach((panel) => {
        panel.element.style.setProperty('--dual-copy-y', `${panel.copyCurrent.toFixed(2)}px`);
        panel.element.style.setProperty('--dual-media-y', `${panel.mediaCurrent.toFixed(2)}px`);
      });
    }

    resetMotion() {
      cancelAnimationFrame(this.measureFrame);
      cancelAnimationFrame(this.motionFrame);
      this.measureFrame = 0;
      this.motionFrame = 0;
      this.panels.forEach((panel) => {
        panel.copyCurrent = 0;
        panel.copyTarget = 0;
        panel.mediaCurrent = 0;
        panel.mediaTarget = 0;
      });
      this.writeTransforms();
    }
  }

  customElements.define('maison-dual-panels', MaisonDualPanels);
}
